import { useEffect, useRef } from "react";
import { ApiResponse, OrderStatus, PaymentMethod, PaymentStatus } from "../stores/index.d";
import { IStripeSessionResponse } from "../stores/cart";
import { IOrder } from "../stores/order";

export const PAYMENT_POLL_INTERVAL_MS = 15000;
const RECENT_ORDER_MS = 2 * 24 * 60 * 60 * 1000;

export const needsPaymentCheck = (order: IOrder, now = Date.now()) =>
  order.paymentMethod === PaymentMethod.Card &&
  order.paymentStatus === PaymentStatus.Pending &&
  !!order.stripeSessionId &&
  [OrderStatus.Placed, OrderStatus.InProgress, OrderStatus.OutForDelivery,
    OrderStatus.ReadyForCollection].includes(order.orderStatus) &&
  // A new checkout on an older running table order updates dateUpdated.
  (new Date(order.orderDate).getTime() >= now - RECENT_ORDER_MS ||
    new Date(order.dateUpdated).getTime() >= now - RECENT_ORDER_MS);

interface PaymentPollingOptions {
  enabled: boolean;
  orders: IOrder[];
  checkPayment: (orderId: number, sessionId: string) =>
    Promise<ApiResponse<IStripeSessionResponse> | undefined>;
  onPaid: () => void | Promise<void>;
}

/** Owned by the page, never by a tab or a paginated list. */
export function useOrderPaymentPolling({ enabled, orders, checkPayment, onPaid }: PaymentPollingOptions) {
  const latest = useRef({ orders, checkPayment, onPaid });
  latest.current = { orders, checkPayment, onPaid };
  const settledSessions = useRef(new Map<string, "paid" | "expired">());

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      let paidOrderChanged = false;
      try {
        const candidates = latest.current.orders.filter(order => needsPaymentCheck(order));
        for (const candidate of candidates) {
          if (cancelled) return;
          // An earlier request / SignalR event may have changed the next order.
          const order = latest.current.orders.find(item => item.orderId === candidate.orderId);
          if (!order || !needsPaymentCheck(order)) continue;
          const sessionId = order.stripeSessionId!;
          const key = `${order.orderId}:${sessionId}`;
          if (settledSessions.current.has(key)) {
            // Retry a failed order-list reload without asking Stripe about a
            // session it has already confirmed paid.
            if (settledSessions.current.get(key) === "paid") paidOrderChanged = true;
            continue;
          }

          try {
            const response = await latest.current.checkPayment(order.orderId, sessionId);
            if (cancelled) return;
            if (!response?.isSuccess || !response.data) continue;
            const current = latest.current.orders.find(item => item.orderId === order.orderId);
            if (current?.stripeSessionId !== sessionId) continue;
            if (response.data.isPaid) settledSessions.current.set(key, "paid");
            else if (response.data.sessionStatus === "expired") settledSessions.current.set(key, "expired");
            if (response.data.isPaid && current.paymentStatus !== PaymentStatus.Paid) {
              paidOrderChanged = true;
            }
          } catch {
            // Leave the order pending. Retry on the next paced cycle, never on render.
          }
        }
        if (!cancelled && paidOrderChanged) await latest.current.onPaid();
      } catch {
        // A failed background list reload must not stop future checks.
      } finally {
        // Wait AFTER completion: even a slow network cannot create overlapping batches.
        if (!cancelled) timer = setTimeout(() => { void poll(); }, PAYMENT_POLL_INTERVAL_MS);
      }
    };

    // Allow the initial order snapshot to load. Tab switches, new array identities
    // and SignalR responses must never run this immediately or reset the timer.
    timer = setTimeout(() => { void poll(); }, PAYMENT_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [enabled]);
}
